import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, Button, Badge, Checkbox, FormControlLabel, Typography, Tooltip, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { getLaceColor, SP_VISIT_COLORS, getSpVisitAge } from "../../constants/clinicalDashboard";
import TableSearchBar from "./TableSearchBar";
import { useObservationsModal } from "../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../components/Advanced/DataGrid/observationsColumn";

/** @keyframes pulse for urgent follow-up badge */
const pulseKeyframes = `
@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.15); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
}
`;

/**
 * TransitionsTable — DataGrid of hospital discharges with LACE scores,
 * follow-up urgency badges, BH indicators, and monthly breakdown.
 *
 * @param {{ discharges: Array, onCreateTask: (row) => void }} props
 */
export default function TransitionsTable({ discharges, onCreateTask }) {
  const navigate = useNavigate();
  const { showObservationsModal } = useObservationsModal();
  const [showExpired, setShowExpired] = useState(false);
  const [search, setSearch] = useState("");
  const [disposition, setDisposition] = useState("All");
  const [monthFilter, setMonthFilter] = useState("All");

  // Collect unique dispositions for the dropdown
  const dispositionOptions = useMemo(() => {
    if (!discharges) return ["All"];
    const unique = [...new Set(discharges.map((r) => r.disposition).filter(Boolean))];
    return ["All", ...unique.sort()];
  }, [discharges]);

  // Collect unique months for the dropdown
  const monthOptions = useMemo(() => {
    if (!discharges) return ["All"];
    const unique = [...new Set(discharges.map((r) => r.admitMonth).filter(Boolean))];
    return ["All", ...unique.sort().reverse()];
  }, [discharges]);

  const filteredRows = useMemo(() => {
    if (!discharges) return [];
    const lowerSearch = search.toLowerCase();
    return discharges.filter((row) => {
      if (!showExpired && row.disposition?.toLowerCase().includes("expired")) return false;
      if (lowerSearch && !row.name?.toLowerCase().includes(lowerSearch) && !row.facility?.toLowerCase().includes(lowerSearch)) return false;
      if (disposition !== "All" && row.disposition !== disposition) return false;
      if (monthFilter !== "All" && row.admitMonth !== monthFilter) return false;
      return true;
    });
  }, [discharges, showExpired, search, disposition, monthFilter]);

  // Month counts for header chips
  const monthCounts = useMemo(() => {
    if (!discharges) return {};
    const counts = {};
    discharges.forEach((r) => {
      if (r.admitMonth) {
        counts[r.admitMonth] = (counts[r.admitMonth] || 0) + 1;
      }
    });
    return counts;
  }, [discharges]);

  const columns = [
    {
      field: "name",
      headerName: "Patient",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "admitMonth",
      headerName: "Month",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        if (!value) return "—";
        const [y, m] = value.split("-");
        const label = new Date(y, m - 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
        return (
          <Chip
            label={label}
            size="small"
            sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, bgcolor: "#EDE9FE", color: "#6D28D9" }}
          />
        );
      },
    },
    {
      field: "dischargeDate",
      headerName: "Discharge",
      width: 100,
    },
    {
      field: "los",
      headerName: "LOS",
      width: 70,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return "—";
        if (val >= 7) {
          return (
            <Chip
              label={val}
              size="small"
              sx={{
                bgcolor: "#FEE2E2",
                color: "#DC2626",
                fontWeight: 700,
                height: 22,
                fontSize: "0.75rem",
              }}
            />
          );
        }
        return val;
      },
    },
    {
      field: "laceScore",
      headerName: "LACE+",
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const val = params.value;
        const { color, label } = getLaceColor(val);
        return (
          <Chip
            label={val ?? "N/A"}
            size="small"
            sx={{
              bgcolor: `${color}18`,
              color,
              fontWeight: 700,
              height: 22,
              fontSize: "0.75rem",
            }}
            title={label}
          />
        );
      },
    },
    {
      field: "behavioralHealth",
      headerName: "BH",
      width: 50,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value ? (
          <PsychologyIcon sx={{ fontSize: 18, color: "#7C3AED" }} />
        ) : null,
    },
    {
      field: "disposition",
      headerName: "Disposition",
      width: 110,
      renderCell: (params) => {
        const val = params.value;
        if (!val) return "—";
        const isExpired = val.toLowerCase().includes("expired");
        return (
          <Box
            component="span"
            sx={{
              color: isExpired ? "#DC2626" : "text.primary",
              fontWeight: isExpired ? 700 : 400,
            }}
          >
            {val}
          </Box>
        );
      },
    },
    {
      field: "hasCarePlan",
      headerName: "Care Plan",
      width: 85,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) =>
        value ? (
          <CheckCircleIcon sx={{ fontSize: 18, color: "#16A34A" }} />
        ) : (
          <CancelIcon sx={{ fontSize: 18, color: "#D1D5DB" }} />
        ),
    },
    {
      field: "isSantePlusPatient",
      headerName: "SP Patient",
      width: 85,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) =>
        value ? (
          <Chip label="Yes" size="small" sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#DCFCE7", color: "#166534" }} />
        ) : (
          <Chip label="No" size="small" sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, bgcolor: "#F1F5F9", color: "#94A3B8" }} />
        ),
    },
    {
      field: "timesContacted",
      headerName: "Contacts",
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value, row }) => {
        const val = value || 0;
        if (val === 0) return <Typography variant="body2" color="text.secondary">0</Typography>;
        return (
          <Chip
            label={val}
            size="small"
            title={row.contactedBy ? `By: ${row.contactedBy}` : ""}
            sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#DBEAFE", color: "#2563EB" }}
          />
        );
      },
    },
    {
      field: "daysSinceDischarge",
      headerName: "Follow-up",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const days = params.value;
        if (days == null) return "—";

        let badgeColor = "default";
        let sx = {};

        if (days <= 2) {
          badgeColor = "error";
          sx = { animation: "pulse 1.2s ease-in-out infinite" };
        } else if (days <= 7) {
          badgeColor = "warning";
        }

        return (
          <Badge
            badgeContent={`${days}d`}
            color={badgeColor}
            sx={sx}
          />
        );
      },
    },
    {
      field: "lastSantePlusVisit",
      headerName: "SP Visit",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const age = getSpVisitAge(value);
        const colors = SP_VISIT_COLORS[age];
        if (!value) {
          return <Chip label="—" size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
        }
        const d = new Date(value);
        const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return <Chip label={label} size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
      },
    },
    {
      field: "observationsCount",
      headerName: "Obs",
      width: 55,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const val = value || 0;
        if (val === 0) return <Box component="span" sx={{ color: "text.secondary" }}>0</Box>;
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#DBEAFE", color: "#2563EB" }} />;
      },
    },
    {
      field: "activeTasks",
      headerName: "Tasks",
      width: 55,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const val = value || 0;
        if (val === 0) return <Box component="span" sx={{ color: "text.secondary" }}>0</Box>;
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#FEF3C7", color: "#D97706" }} />;
      },
    },
    getObservationsColumn(showObservationsModal, 3),
    {
      field: "viewPatient",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="View Patient">
          <IconButton size="small" onClick={() => navigate(`/patient-info/${params.row.cipId}`)} sx={{ color: "#6366F1" }}>
            <VisibilityIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onCreateTask(params.row)}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          Follow-up
        </Button>
      ),
    },
  ];

  return (
    <Box>
      {/* Inject pulse keyframes */}
      <style>{pulseKeyframes}</style>

      {/* Monthly patient counts summary */}
      {Object.keys(monthCounts).length > 0 && (
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mr: 1 }}>
            Patients by month:
          </Typography>
          {Object.entries(monthCounts)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([month, count]) => {
              const [y, m] = month.split("-");
              const label = new Date(y, m - 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
              return (
                <Chip
                  key={month}
                  label={`${label}: ${count}`}
                  size="small"
                  onClick={() => setMonthFilter(monthFilter === month ? "All" : month)}
                  variant={monthFilter === month ? "filled" : "outlined"}
                  color={monthFilter === month ? "primary" : "default"}
                  sx={{ fontWeight: 600, fontSize: "0.72rem", cursor: "pointer" }}
                />
              );
            })}
          {monthFilter !== "All" && (
            <Chip
              label="Clear"
              size="small"
              onDelete={() => setMonthFilter("All")}
              sx={{ fontSize: "0.72rem" }}
            />
          )}
        </Box>
      )}

      {/* Search bar + Disposition dropdown + Month dropdown */}
      <TableSearchBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search patient or facility..."
        filters={[
          { key: "disposition", label: "Disposition", options: dispositionOptions, value: disposition },
          { key: "month", label: "Month", options: monthOptions, value: monthFilter },
        ]}
        onFilterChange={(key, value) => {
          if (key === "disposition") setDisposition(value);
          if (key === "month") setMonthFilter(value);
        }}
      />

      {/* Show Expired toggle */}
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={showExpired}
            onChange={(e) => setShowExpired(e.target.checked)}
          />
        }
        label="Show Expired"
        slotProps={{ typography: { variant: "body2" } }}
        sx={{ mb: 1 }}
      />

      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.cipId}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          borderRadius: 2,
          border: "none",
          "& .MuiDataGrid-cell": {
            fontSize: "0.78rem",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            fontSize: "0.78rem",
          },
        }}
      />
    </Box>
  );
}
